import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-bold">Terms of Service</h1>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-slate max-w-none">
          <p className="text-sm text-slate-500 mb-6">Last Updated: January 11, 2026</p>

          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using ProjectStatusReport.com ("Service"), you agree to be bound 
            by these Terms of Service ("Terms"). If you disagree with any part of these terms, 
            you may not access the Service.
          </p>

          <h2>2. Description of Service</h2>
          <p>
            ProjectStatusReport.com provides a web-based platform for creating, managing, 
            and sharing project status reports. The Service includes report generation, 
            PDF export, project tracking, and related features.
          </p>

          <h2>3. User Accounts</h2>
          <ul>
            <li>You must provide accurate and complete registration information</li>
            <li>You are responsible for maintaining the security of your account</li>
            <li>You must notify us immediately of any unauthorized access</li>
            <li>You may not share your account credentials</li>
            <li>One person or entity per account</li>
          </ul>

          <h2>4. Subscription Plans</h2>
          
          <h3>Free Trial</h3>
          <ul>
            <li>14-day trial period</li>
            <li>Limited to 1 project</li>
            <li>PDF exports include watermark</li>
            <li>No credit card required</li>
          </ul>

          <h3>Professional Plan ($29/month or $290/year)</h3>
          <ul>
            <li>Up to 5 projects</li>
            <li>PDF export without watermark</li>
            <li>Full report history</li>
            <li>Bulk PDF export</li>
          </ul>

          <h3>Business Plan ($49/month or $490/year)</h3>
          <ul>
            <li>Up to 15 projects</li>
            <li>All Professional features</li>
            <li>Shareable report URLs</li>
            <li>Priority support</li>
          </ul>

          <h2>5. Payment Terms</h2>
          <ul>
            <li>Payments are processed securely by Stripe</li>
            <li>Subscriptions auto-renew unless cancelled</li>
            <li>Annual plans are billed upfront</li>
            <li>Prices may change with 30 days notice</li>
            <li>No refunds for partial periods</li>
          </ul>

          <h2>6. Cancellation</h2>
          <ul>
            <li>You may cancel your subscription at any time</li>
            <li>Access continues until the end of your billing period</li>
            <li>After cancellation, you have 90 days of read-only access</li>
            <li>You may export your data during this period</li>
            <li>Data is deleted 90 days after subscription ends</li>
          </ul>

          <h2>7. User Content</h2>
          <ul>
            <li>You retain ownership of all content you create</li>
            <li>You grant us a license to store and display your content</li>
            <li>You are responsible for the accuracy of your content</li>
            <li>You may not upload illegal or harmful content</li>
          </ul>

          <h2>8. Prohibited Uses</h2>
          <p>You may not:</p>
          <ul>
            <li>Violate any laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Transmit malware or harmful code</li>
            <li>Attempt to gain unauthorized access</li>
            <li>Interfere with the Service's operation</li>
            <li>Resell or redistribute the Service</li>
            <li>Use automated systems to access the Service</li>
          </ul>

          <h2>9. Intellectual Property</h2>
          <p>
            The Service and its original content (excluding user content) are owned by 
            Instructing.com LLC. Our trademarks may not be used without written permission.
          </p>

          <h2>10. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT 
            GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
          </p>

          <h2>11. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, INSTRUCTING.COM LLC SHALL NOT BE 
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE 
            DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.
          </p>

          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Instructing.com LLC from any claims, 
            damages, or expenses arising from your use of the Service or violation of 
            these Terms.
          </p>

          <h2>13. Modifications to Service</h2>
          <p>
            We reserve the right to modify or discontinue the Service at any time. 
            We will provide reasonable notice of significant changes.
          </p>

          <h2>14. Changes to Terms</h2>
          <p>
            We may update these Terms periodically. Continued use of the Service 
            after changes constitutes acceptance of the new Terms.
          </p>

          <h2>15. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the United States. Any disputes 
            shall be resolved in the courts of [State].
          </p>

          <h2>16. Contact</h2>
          <p>
            For questions about these Terms:<br />
            Email: <a href="mailto:cs@instructing.com" className="text-blue-600">cs@instructing.com</a><br />
            Instructing.com LLC<br />
            United States
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-auto py-6 text-center text-sm text-slate-500">
        <div className="flex justify-center gap-4">
          <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-slate-700">Terms of Service</Link>
          <span>·</span>
          <a href="mailto:cs@instructing.com" className="hover:text-slate-700">Contact</a>
        </div>
        <p className="mt-2">© 2026 Instructing.com LLC. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default TermsPage;
